import { useRef } from "react";
import { useForm, useWatch } from "react-hook-form";

import {
  type ConstraintListItemDto,
  type PartnerCategoriesDto,
  type ProjectConnectionDetailsDto,
  type ProjectConnectionDto,
  type UserRoleServicePartnerPagePermissionsDto,
} from "@/api/types";
import bgPlaceholder from "@/assets/images/image-placeholder.png";
import { AudienceSelector } from "@/components/AudienceSelector/AudienceSelector";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Form } from "@/components/Form/Form";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormRadio, FormRadioGroup } from "@/components/Form/FormRadio";
import { FormRichTextEditor } from "@/components/Form/FormRichTextEditor";
import { FormSelect } from "@/components/Form/FormSelect";
import { Translate01Icon } from "@/components/Icons/Translate01Icon";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { WithAppPreviewLayout } from "@/components/Layouts/WithAppPreviewLayout";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PermissionSelector } from "@/components/PermissionSelector/PermissionSelector";
import { AppServicePartnerDetailsView } from "@/components/Phone/AppServicePartnerDetailsView";
import { Phone } from "@/components/Phone/Phone";
import type { RichTextEditorRef } from "@/components/RichTextEditor/RichTextEditor";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { getFlagIcon } from "@/helpers/flags";
import type { FormPermissions } from "@/helpers/permissions";
import {
  createEmailValidRule,
  createImageSizeRule,
  createImageTypeRule,
  createRequiredStringRule,
} from "@/helpers/rules";
import { isValidPhoneNumber } from "@/helpers/validation";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import type { Languages } from "@/translations";
import { useTranslation } from "@/translations";

type ServicePartnerType = "general" | "iqbi" | "myPup" | "bringme";

export interface ServicePartnerFormProps {
  isEditMode: boolean;
  allowedTypes: ServicePartnerType[];
  categories: PartnerCategoriesDto[];
  defaultValues: CreateOrEditServicePartnerFormValues;
  isSubmitting: boolean;
  connections: ProjectConnectionDetailsDto[];
  contentLanguageId: Languages | undefined;
  onSubmit: (data: CreateOrEditServicePartnerFormValues) => void;
}

const MAX_LENGTH = 128;

export function ServicePartnerForm({
  defaultValues,
  isSubmitting,
  connections,
  allowedTypes,
  isEditMode,
  contentLanguageId,
  onSubmit,
  categories,
}: ServicePartnerFormProps): React.ReactNode {
  const editorRef = useRef<RichTextEditorRef>(null);
  const { t } = useTranslation();
  const user = useSessionUser();
  const slug = useSlug();
  const sessionUser = useSessionUser();
  const formMethods = useForm<CreateOrEditServicePartnerFormValues>({
    mode: "onChange",
    defaultValues: defaultValues,
  });

  const connection = useWatch<CreateOrEditServicePartnerFormValues, "connection">({
    control: formMethods.control,
    name: "connection",
  });
  const audience = useWatch<CreateOrEditServicePartnerFormValues, "audience">({
    control: formMethods.control,
    name: "audience",
  });
  const permissions = useWatch<CreateOrEditServicePartnerFormValues, "permissions">({
    control: formMethods.control,
    name: "permissions",
  });

  const showIntegrationOptions = isEditMode
    ? defaultValues.type !== "general"
    : allowedTypes.length > 1 || !allowedTypes.includes("general");
  const title = isEditMode
    ? t("page.service.create-or-edit.edit-mode.title")
    : t("page.service.create-or-edit.create-mode.title");

  const flagIcon = getFlagIcon({
    languageId: contentLanguageId || sessionUser.uiLanguage.id,
    width: 16,
  });

  const permissionFields = [{ key: "canManageContent", label: t("model.permission.service-page.can-manage-content") }];

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={title}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.service.list.title"),
              to: routes.servicePartners.list({
                slug,
              }),
            },
            {
              name: title,
            },
          ]}
        />
      }
    >
      <Form.Root formMethods={formMethods} onSubmit={onSubmit}>
        <WithAppPreviewLayout preview={<Preview />}>
          <Form.Container>
            <Form.Body>
              <Notice
                icon={<Translate01Icon />}
                variant="special"
                message={
                  isEditMode
                    ? t("common.label.you-are-editing-in-original-content-language")
                    : t("common.label.your-input-will-be-auto-translated")
                }
              />
              {showIntegrationOptions && (
                <FormField label={t("model.service.type")}>
                  <FormRadioGroup>
                    {(defaultValues.type === "general" || allowedTypes.includes("general")) && (
                      <FormRadio<CreateOrEditServicePartnerFormValues>
                        name="type"
                        value="general"
                        label={t("model.service.type.general")}
                        rules={{
                          required: {
                            message: t("components.form.error.required", {
                              inputName: t("model.service.type"),
                            }),
                            value: true,
                          },
                        }}
                        disabled={isEditMode}
                      />
                    )}
                    {(defaultValues.type === "iqbi" || allowedTypes.includes("iqbi")) && (
                      <FormRadio<CreateOrEditServicePartnerFormValues>
                        name="type"
                        value="iqbi"
                        label={t("model.service.type.iqbi")}
                        rules={{
                          required: {
                            message: t("components.form.error.required", {
                              inputName: t("model.service.type"),
                            }),
                            value: true,
                          },
                        }}
                        disabled={isEditMode}
                      />
                    )}
                    {(defaultValues.type === "myPup" || allowedTypes.includes("myPup")) && (
                      <FormRadio<CreateOrEditServicePartnerFormValues>
                        name="type"
                        value="myPup"
                        label={t("model.service.type.mypup")}
                        rules={{
                          required: {
                            message: t("components.form.error.required", {
                              inputName: t("model.service.type"),
                            }),
                            value: true,
                          },
                        }}
                        disabled={isEditMode}
                      />
                    )}
                    {(defaultValues.type === "bringme" || allowedTypes.includes("bringme")) && (
                      <FormRadio<CreateOrEditServicePartnerFormValues>
                        name="type"
                        value="bringme"
                        label={t("model.service.type.bringme")}
                        rules={{
                          required: {
                            message: t("components.form.error.required", {
                              inputName: t("model.service.type"),
                            }),
                            value: true,
                          },
                        }}
                        disabled={isEditMode}
                      />
                    )}
                  </FormRadioGroup>
                </FormField>
              )}
              <FormField label={t("model.service.title")} actions={flagIcon} required>
                <FormInput<CreateOrEditServicePartnerFormValues>
                  name="title"
                  placeholder={t("model.service.title.placeholder")}
                  data-testid="service-name"
                  rules={{
                    maxLength: {
                      message: t("components.form.error.max-length", {
                        length: MAX_LENGTH,
                      }),
                      value: MAX_LENGTH,
                    },
                    validate: {
                      required: createRequiredStringRule(t, "model.service.title"),
                    },
                  }}
                />
              </FormField>
              <FormField
                label={t("model.service.subtitle")}
                description={t("model.service.subtitle.description")}
                actions={flagIcon}
                required
              >
                <FormInput<CreateOrEditServicePartnerFormValues>
                  name="subtitle"
                  placeholder={t("model.service.subtitle.placeholder")}
                  data-testid="service-slogan"
                  rules={{
                    maxLength: {
                      message: t("components.form.error.max-length", {
                        length: MAX_LENGTH,
                      }),
                      value: MAX_LENGTH,
                    },
                    validate: {
                      required: createRequiredStringRule(t, "model.service.subtitle"),
                    },
                  }}
                />
              </FormField>
              <FormField label={t("model.service.description")} actions={flagIcon} required>
                <FormRichTextEditor<CreateOrEditServicePartnerFormValues>
                  editorRef={(ref) => {
                    editorRef.current = ref;
                  }}
                  name="description"
                  placeholder={t("model.service.description.placeholder")}
                  data-testid="description-input"
                  rules={{
                    required: {
                      message: t("components.form.error.required", {
                        inputName: t("model.service.description"),
                      }),
                      value: true,
                    },
                  }}
                />
              </FormField>
              {defaultValues.type === "iqbi" && (
                <FormField label={t("model.service.type")} required>
                  <FormRadioGroup>
                    <FormRadio<CreateOrEditServicePartnerFormValues, "type">
                      name="type"
                      value="general"
                      label={t("model.service.type.general")}
                      disabled
                    />
                    <FormRadio<CreateOrEditServicePartnerFormValues, "type">
                      name="type"
                      value="iqbi"
                      label={t("model.service.type.iqbi")}
                      disabled
                    />
                  </FormRadioGroup>
                </FormField>
              )}
              <FormField
                label={t("model.service.category")}
                description={t("model.service.category.description")}
                required
              >
                <FormSelect<CreateOrEditServicePartnerFormValues, PartnerCategoriesDto>
                  name="category"
                  placeholder={t("model.service.category.placeholder")}
                  items={categories}
                  keySelector={({ id }) => id}
                  renderOption={(tab) => tab.name}
                  data-testid="service-category"
                  rules={{
                    required: {
                      message: t("components.form.error.required", {
                        inputName: t("model.service.category"),
                      }),
                      value: true,
                    },
                  }}
                />
              </FormField>
              <FormField label={t("model.service.email")} description={t("model.service.email.description")}>
                <FormInput<CreateOrEditServicePartnerFormValues, "email">
                  name="email"
                  type="email"
                  inputMode="email"
                  data-testid="service-email"
                  rules={{
                    validate: {
                      isValid: createEmailValidRule(t),
                    },
                  }}
                />
              </FormField>
              <FormField
                label={t("model.service.phone-number")}
                description={t("model.service.phone-number.description")}
              >
                <FormInput<CreateOrEditServicePartnerFormValues, "phoneNumber">
                  name="phoneNumber"
                  type="tel"
                  inputMode="tel"
                  data-testid="service-phone"
                  rules={{
                    validate: {
                      isValid(value) {
                        value = value?.trim();
                        if (!value) {
                          return;
                        }

                        return isValidPhoneNumber(value) ? undefined : t("components.form.error.invalid-phone-number");
                      },
                    },
                  }}
                />
              </FormField>
              <FormField
                label={t("model.service.profile-image")}
                description={t("model.service.profile-image.description")}
                required
              >
                <FormImageInput<CreateOrEditServicePartnerFormValues, "profileImage">
                  name="profileImage"
                  cropper="fill"
                  data-testid="service-profile-image"
                  rules={{
                    required: t("components.form.error.required", {
                      inputName: t("model.service.profile-image"),
                    }),
                    validate: {
                      size: createImageSizeRule({
                        t,
                      }),
                      type: createImageTypeRule({
                        t,
                      }),
                    },
                  }}
                />
              </FormField>
              <FormField
                label={t("model.service.header-image")}
                description={t("model.service.header-image.description")}
              >
                <FormImageInput<CreateOrEditServicePartnerFormValues, "headerImage">
                  name="headerImage"
                  id="headerImage"
                  data-testid="service-header-image"
                  rules={{
                    validate: {
                      size: createImageSizeRule({
                        t,
                      }),
                      type: createImageTypeRule({
                        t,
                      }),
                    },
                  }}
                />
              </FormField>
              {user.isAdmin && (
                <Stack gap="sm">
                  <FormField
                    label={t("common.entity.audience")}
                    description={t("component.audience-section.description", {
                      audienceTarget: t("common.entity.service").toLowerCase(),
                    })}
                  >
                    {connections.length > 0 && (
                      <FormField label={t("page.service.create-or-edit.connection.label")}>
                        <FormSelect<CreateOrEditServicePartnerFormValues, ProjectConnectionDto>
                          name="connection"
                          items={connections}
                          keySelector={(x) => x.id}
                          renderOption={(x) => x.name}
                          emptyItem={t("page.service.create-or-edit.connection.none")}
                        />
                      </FormField>
                    )}
                  </FormField>
                  {!connection && (
                    <AudienceSelector
                      defaultAudience={audience}
                      isEditMode={isEditMode}
                      onSave={(newAudience) => formMethods.setValue("audience", newAudience)}
                    />
                  )}
                </Stack>
              )}
              {user.isSuperAdmin && (
                <FormField
                  label={t("component.permission-section.title")}
                  description={t("component.permission-section.description", {
                    permissionTarget: t("common.entity.service").toLowerCase(),
                  })}
                >
                  {connection ? (
                    <p className="py-space-lg pl-space-sm text-text-secondary">
                      {t("page.service.create-or-edit.permissions.disabled-for-connection")}
                    </p>
                  ) : (
                    <PermissionSelector
                      defaultPermissions={permissions}
                      fields={permissionFields}
                      onSave={(newPermissions) => formMethods.setValue("permissions", newPermissions)}
                    />
                  )}
                </FormField>
              )}
            </Form.Body>
            <Form.Footer
              renderPrimaryAction={() => (
                <Button type="submit" isLoading={isSubmitting}>
                  {isEditMode ? t("common.action.save") : t("common.action.create")}
                </Button>
              )}
            />
          </Form.Container>
        </WithAppPreviewLayout>
      </Form.Root>
    </DocumentPage>
  );
}
export interface CreateOrEditServicePartnerFormValues {
  title: string;
  subtitle: string;
  description: string;
  audience: ConstraintListItemDto[];
  connection?: ProjectConnectionDto;
  permissions: FormPermissions<UserRoleServicePartnerPagePermissionsDto>;
  category?: PartnerCategoriesDto;
  email?: string;
  phoneNumber?: string;
  profileImage?: FormImage[];
  headerImage?: FormImage[];
  type: ServicePartnerType;
}
function Preview() {
  const user = useSessionUser();
  const { email, phoneNumber, title, subtitle, description, profileImage, headerImage } =
    useWatch<CreateOrEditServicePartnerFormValues>();

  return (
    <Phone>
      <AppServicePartnerDetailsView
        name={title ?? ""}
        slogan={subtitle}
        description={description ?? ""}
        profileImage={profileImage?.[0]?.url}
        headerImage={headerImage?.[0]?.url ?? user.project.backgroundImage?.url ?? bgPlaceholder}
        email={email}
        phoneNumber={phoneNumber}
        projectTintColor={user.project?.styling.tintColor}
      />
    </Phone>
  );
}
